import React, { useEffect, useMemo, useRef, useState } from "react";
import Table from "../../components/utils/Table";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import Button from "../../components/utils/Button";
import { downloadExcelSampleFile } from "../../helpers/commonFunction";
import CardLayout from "../../components/utils/CardLayout";
import ToastAlert from "../../components/utils/ToastAlert";
import planPriceConfigActions from "../../redux/planPriceMapping/actions";


const { getPlanPriceConfigList, resetMessage, updatePlanPriceConfig, updateSIMSwapPriceConfig } = planPriceConfigActions;

const transactionTypeList = [
  {
    title: 'Activation',
    value: 1
  },
  {
    title: 'Recharge',
    value: 2
  },
  {
    title: 'SIM Swap',
    value: 3
  }
]

const PlanPriceMapping = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [transactionType, setTransactionType] = useState(1);
  const [searchItem, setSearchItem] = useState("");
  const {
    planPriceConfigList,
    planPriceConfigLoading,
    planPriceConfigError,
    updatePlanPriceConfigMessage,
    updatePlanPriceConfigLoading,
    updatePlanPriceConfigError } = useSelector((state) => state.planPriceConfig);
  const [updatePriceFlag, setUpdatePriceFlag] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [vailidError, setVailidError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const { register, handleSubmit, reset, setValue, watch, formState: { errors }, } = useForm({
    mode: "onChange",
    defaultValues: {
    },
  });

  useEffect(() => {
    return () => {
      dispatch(resetMessage());
    };
  }, []);


  useEffect(() => {
    dispatch(getPlanPriceConfigList({ actionType: transactionType }))
  }, [transactionType])

  useEffect(() => {
    if (planPriceConfigError) {
      ToastAlert({ type: "error", text: planPriceConfigError, });
    }
    if (updatePlanPriceConfigError) {
      ToastAlert({ type: "error", text: updatePlanPriceConfigError, });
    }
    if (updatePlanPriceConfigMessage) {
      ToastAlert({ type: "success", text: updatePlanPriceConfigMessage, });
      dispatch(getPlanPriceConfigList({ actionType: transactionType }))
      handleBack();
    }
  }, [planPriceConfigError, updatePlanPriceConfigMessage, updatePlanPriceConfigError]);


  const handleUpdatePlanPrice = () => {
    setUpdatePriceFlag(true)
  }

  const columns = useMemo(() => {
    return Array.isArray(planPriceConfigList) && planPriceConfigList.length > 0
      ? Object.keys(planPriceConfigList[0]).map((item) => ({
        displayName: item,
        key: item,
      }))
      : [];
  }, [planPriceConfigList]);

  const filteredItems = useMemo(() => {
    return Array.isArray(planPriceConfigList)
      ? planPriceConfigList.filter((item) => {
        if (!searchItem) return true;

        return Object.values(item).some(
          (value) =>
            typeof value === "string" &&
            value.toLowerCase().includes(searchItem.toLowerCase())
        );
      })
      : [];
  }, [planPriceConfigList, searchItem]);

  const dataSource = useMemo(() => {
    if (filteredItems && filteredItems.length > 0) {

    }
    return filteredItems.map((item, index) => {
      const row = {};
      columns.forEach((clm) => {
        row[clm.key] = item[clm.key];
      });

      return row;
    });
  }, [filteredItems, columns]);


  const downloadPlanConfigFile = () => {
    if (!filteredItems?.length || !columns?.length) {
      return;
    }

    const fileType = transactionType == 1 ? "Activation" : transactionType == 2 ? "Recharge" : "SIMSwap";

    const data = filteredItems.map((item) => {
      const row = {
        Type: fileType,
      };

      columns.forEach((column) => {
        row[column.displayName] = item[column.key] ?? "";
      });

      return row;
    });


    const filename = transactionType == 1 ? "activation-plan-price-config.xlsx" : transactionType == 2 ? "recharge-plan-price-config.xlsx" : "sim-swap-plan-price-config.xlsx";
    downloadExcelSampleFile(data, filename);
  };

  const onFileUpload = (e) => {
    const fileReader = new FileReader();
    let file = e.target.files[0];
    setSelectedFile(null);
    setVailidError("");
    setPreviewData([]);
    if (!file) return;
    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setVailidError("Please upload a valid file (.xlsx, .xls)");
      return;
    }

    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      // Get first sheet
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      // Convert to JSON (rows)
      const jsonData = XLSX.utils.sheet_to_json(worksheet, {
        defval: "", // avoid undefined
      });

      // Get columns (headers)
      const headers = Object.keys(jsonData[0] || {});

      const isValid = headers.length === (columns.length + 1) && columns.every((col) => headers.includes(col?.key));

      if (!isValid) {
        setVailidError("Invalid file. required columns are missing or incorrect.",);
        return;
      }

      const hasInvalidData = jsonData.some((row) =>
        Object.values(row).some((value) => value === "" || value === null || value === undefined)
      );

      // Invalid Type check
      if (hasInvalidData) {
        setVailidError("Invalid file. All fields are required and cannot be blank.");
        return;
      }

      // Check Type for every row
      const stateValues = jsonData.map((row) => getStateValue(row.Type));

      // Invalid Type check
      if (stateValues.some((value) => value === null)) {
        setVailidError("Invalid type. type must be activation, recharge or SIMSwap.");
        return;
      }

      // Get unique Type values from the file
      const uniqueStateValues = [...new Set(stateValues)];

      // File should contain only ONE type
      if (uniqueStateValues.length !== 1) {
        setVailidError("Invalid file. activation, recharge or SIMSwap data cannot be mixed.");
        return;
      }

      // Check uploaded file type against current transaction type
      if (uniqueStateValues[0] !== transactionType) {
        setVailidError(
          transactionType == 1 ? "Invalid file. please upload an activation file."
            : transactionType == 2 ? "invalid file. please upload a recharge file."
              : "Invalid file. please upload a SIM Swap file."
        );
        return;
      }

      const hasNegative = hasNegativeValue(jsonData);
      if (hasNegative) {
        setVailidError("Invalid file. negative values are not allowed.");
        return;
      }

      const fileData = jsonData.map(({ Type, ...row }) => row);

      const newWorksheet = XLSX.utils.json_to_sheet(fileData);
      const newWorkbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, transactionType == 1 ? "Activation" : transactionType == 2 ? "Recharge" : "SIMSwap");
      const excelBuffer = XLSX.write(newWorkbook, {
        bookType: "xlsx",
        type: "array",
      });

      const newFile = new File(
        [excelBuffer],
        file.name,
        {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }
      );

      setSelectedFile(newFile);
      setPreviewData(fileData);
    };

  };


  const getStateValue = (type) => {
    const value = type?.toString().trim().toLowerCase();
    if (value === "activation") {
      return 1;
    }

    if (value === "recharge") {
      return 2;
    }

    if (value === "simswap") {
      return 3;
    }

    return null;
  };


  const hasNegativeValue = (data) => {
    return data.some((row) =>
      Object.values(row).some((value) => {
        if (value === "" || value === null || value === undefined) {
          return false;
        }

        const numberValue = Number(value);

        return !isNaN(numberValue) && numberValue < 0;
      })
    );
  };

  const onSubmit = () => {
    const formData = new FormData();
    formData.append("actionType", transactionType);

    if (selectedFile) {
      formData.append("file", selectedFile);
    } else {
      formData.append("file", "");
    }

    if (transactionType == 3) {
      dispatch(updateSIMSwapPriceConfig(formData));
      return;
    }

    dispatch(updatePlanPriceConfig(formData));
  };


  const handleReset = () => {
    setVailidError("");
    setPreviewData([]);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const handleBack = () => {
    setUpdatePriceFlag(false)
    handleReset()
  }


  return (
    <CardLayout title={updatePriceFlag ? "Update Plan Discounting & Employee Commission" : "Plan Discounting & Employee Commission"} icon="fas fa-link"
      rightContent={
        <div className="d-flex gap-2">
          <div className="dropdown" style={{ marginTop: '2px' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm btn-wave waves-effect waves-light"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {transactionTypeList?.find(
                (item) => item?.value === transactionType
              )?.title || "Select Transaction Type"}

              <i className="ri-arrow-down-s-line align-middle ms-1 d-inline-block" />
            </button>

            <ul className="dropdown-menu dropdown-menu-end" role="menu">
              {transactionTypeList?.map((item) => (
                <li key={item?.value}>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => setTransactionType(item?.value)}
                  >
                    {item?.title}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          {updatePriceFlag ? <>
            <Button
              title="Plan Config"
              buttonType="button"
              icon={<i className="ri-download-cloud-line me-1" />}
              variant="secondary"
              style={{ padding: "5px 10px", }}
              onClick={downloadPlanConfigFile}
            />
            <Button
              title="Back"
              buttonType="button"
              icon={<i className="ri-arrow-left-line me-1" />}
              variant="primary"
              style={{ padding: "5px 10px", }}
              onClick={() => handleBack()}
            />
          </> :
            <Button
              title="Update Plan Config"
              buttonType="button"
              icon={<i className="ri-refresh-line me-1" />}
              variant="secondary"
              style={{ padding: "5px 10px", }}
              onClick={() => handleUpdatePlanPrice()}
            />}
        </div>
      }
    >

      {/* Table */}
      {!updatePriceFlag ? <div>
        <Table
          columns={columns}
          loading={planPriceConfigLoading}
          data={dataSource}
          pagination={true}
          tableHeight="380px"
        />
      </div> : <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">

            <div className="form-group col-lg-3 col-md-4">
              <label className="form-label fw-semibold">
                Upload File <span className="text-danger">*</span>
              </label>

              {/* Hidden File Input */}
              <input
                type="file"
                ref={fileInputRef}
                accept=".xlsx,.xls"
                className="form-control"
                id="fileUpload"
                onChange={onFileUpload}
              />
              {vailidError && (<div className="" style={{ color: "#dc3545", fontSize: "12px", marginTop: "2px", }} > {vailidError} </div>)}
            </div>

            {/* Actions */}
            <div className="col-md-2 d-flex align-items-end" style={{ height: '70px' }}>
              <div className="d-flex gap-3">
                <Button
                  title="Update"
                  buttonType="submit"
                  variant="primary"
                  loading={updatePlanPriceConfigLoading}
                  icon={<i className="ri-send-plane-fill me-2"></i>}
                  disable={previewData?.length == 0}
                />

                <Button
                  title="Reset"
                  buttonType="button"
                  variant="secondary"
                  disable={updatePlanPriceConfigLoading}
                  icon={<i className="ri-refresh-line me-2"></i>}
                  onClick={handleReset}
                />
              </div>
            </div>
          </div>
        </form>

        <div className="mt-3">
          <Table
            columns={columns}
            loading={false}
            data={previewData}
            pagination={true}
            tableHeight="520px"
          />
        </div>
      </div>}

    </CardLayout>

  )
}

export default PlanPriceMapping
